import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import type { RegisterRequest } from '@/api/auth';
import { userApi } from '@/api/user';
import type { Gender } from '@/api/user';
import { ApiError, setSessionExpiredHandler } from '@/api/client';
import type { MeResponse, User } from '@/types/User';

function clearUserScopedStorage() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('ef_')) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage может быть недоступен (privacy mode) — молча игнорируем
  }
}

function meResponseToUser(me: MeResponse): User | null {
  if (!me.authenticated) return null;
  const name = me.name ?? me.username ?? me.email ?? 'User';
  return {
    name,
    email: me.email ?? '',
    username: me.username,
    language: me.language,
    gender: me.gender,
  };
}

interface AuthResult {
  ok: boolean;
  message?: string;
  error?: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  register: (data: RegisterRequest) => Promise<AuthResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  updateGender: (gender: Gender) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const meQueryKey = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const meQuery = useQuery<User | null, Error>({
    queryKey: meQueryKey,
    queryFn: async () => {
      try {
        const me = await authApi.me();
        const base = meResponseToUser(me);
        if (!base) return null;
        try {
          // skipAuthHandler: профиль терпим к ошибке (см. catch) — не должен сам
          // инициировать разлогин во время первичной загрузки.
          const profile = await userApi.me({ skipAuthHandler: true });
          return { ...base, gender: profile.gender ?? base.gender };
        } catch (err) {
          // профиль может быть ещё не создан (Kafka-консьюмер опаздывает) — auth-state важнее
          console.warn('user profile fetch failed', err);
          return base;
        }
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return null;
        }
        console.warn('auth refresh failed', err);
        return null;
      }
    },
    staleTime: Infinity,
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: meQueryKey });
  }, [qc]);

  // Любой API-запрос, вернувший 401/403, означает что сессия на бэке умерла.
  // Сбрасываем auth-state (→ RequireAuth уводит на /login) и помечаем причину,
  // чтобы логин показал «session expired». Без этого UI продолжал бы считать
  // юзера залогиненным (meQuery со staleTime: Infinity), а действия молча падали.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      queueMicrotask(() => {
        if (qc.getQueryData(meQueryKey) == null) return; // уже разлогинены
        try {
          sessionStorage.setItem('ef_session_expired', '1');
        } catch {
          // sessionStorage недоступен (privacy mode) — не критично
        }
        qc.setQueryData(meQueryKey, null);
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
        clearUserScopedStorage();
      });
    });
    return () => setSessionExpiredHandler(null);
  }, [qc]);

  useEffect(() => {
    const gender = meQuery.data?.gender;
    if (gender) {
      document.documentElement.dataset.gender = gender;
    } else {
      delete document.documentElement.dataset.gender;
    }
  }, [meQuery.data?.gender]);

  const loginMut = useMutation({
    mutationFn: (vars: { email: string; password: string }) => authApi.login(vars),
  });
  const registerMut = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
  });
  const logoutMut = useMutation({
    mutationFn: () => authApi.logout(),
  });

  const login = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      try {
        const res = await loginMut.mutateAsync({ email, password });
        if (res.authenticated) {
          // на случай смены юзера: чужие ef_* кэши не должны мелькать до фетча
          clearUserScopedStorage();
          qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
          await refresh();
          return { ok: true };
        }
        return { ok: false, message: res.message ?? 'Login failed', error: res.error };
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Network error';
        return { ok: false, message: msg };
      }
    },
    [loginMut, qc, refresh],
  );

  const register = useCallback(
    async (data: RegisterRequest): Promise<AuthResult> => {
      try {
        const res = await registerMut.mutateAsync(data);
        if (res.authenticated) {
          clearUserScopedStorage();
          qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
          await refresh();
          return { ok: true };
        }
        return { ok: false, message: res.message ?? 'Registration failed', error: res.error };
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Network error';
        return { ok: false, message: msg };
      }
    },
    [registerMut, qc, refresh],
  );

  const updateGender = useCallback(
    async (gender: Gender) => {
      const updated = await userApi.update({ gender });
      qc.setQueryData<User | null>(meQueryKey, (prev) =>
        prev ? { ...prev, gender: updated.gender ?? '' } : prev,
      );
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await logoutMut.mutateAsync();
    } catch {
      // если logout упал — всё равно сбрасываем локальный state
    }
    qc.setQueryData(meQueryKey, null);
    // протухаем все остальные кэши: после logout их данные больше не наши
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    clearUserScopedStorage();
  }, [logoutMut, qc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      loading: meQuery.isPending,
      login,
      register,
      logout,
      refresh,
      updateGender,
    }),
    [meQuery.data, meQuery.isPending, login, register, logout, refresh, updateGender],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
